import { useEffect, useMemo, useState } from "react";
import { FiPlus, FiSave, FiTrash2 } from "react-icons/fi";

const defaultConfig = {
 image:
  "/assets/scooty-feature-02.png",
 active: true,
 features: [
  {
   id: "display",
   label: "Smart Led\nDisplay",
   x: 41,
   y: 26,
   labelX: 17,
   labelY: 13,
   anchorX: 36,
   anchorY: 20,
   align: "right",
   active: true,
  },
  {
   id: "cup",
   label: "Handy Cup\nHolder",
   x: 32,
   y: 35,
   labelX: 12,
   labelY: 34,
   anchorX: 29,
   anchorY: 35,
   align: "right",
   active: true,
  },
  {
   id: "body",
   label: "Sturdy\nBody",
   x: 33,
   y: 46,
   labelX: 11,
   labelY: 46,
   anchorX: 29,
   anchorY: 47,
   align: "right",
   active: true,
  },
  {
   id: "foot",
   label: "Convenient Foot\nSpace",
   x: 40,
   y: 62,
   labelX: 12,
   labelY: 61,
   anchorX: 33,
   anchorY: 62,
   align: "right",
   active: true,
  },
  {
   id: "tires",
   label: "Durable Tires",
   x: 32,
   y: 78,
   labelX: 10,
   labelY: 84,
   anchorX: 28,
   anchorY: 80,
   align: "right",
   active: true,
  },
  {
   id: "stand",
   label: "Easy Stand",
   x: 47,
   y: 84,
   labelX: 35,
   labelY: 91,
   anchorX: 44,
   anchorY: 87,
   align: "center",
   active: true,
  },
  {
   id: "suspension",
   label: "Smooth\nSuspension",
   x: 54,
   y: 76,
   labelX: 57,
   labelY: 90,
   anchorX: 55,
   anchorY: 82,
   align: "center",
   active: true,
  },
  {
   id: "motor",
   label: "Efficient Motor",
   x: 61,
   y: 77,
   labelX: 78,
   labelY: 81,
   anchorX: 66,
   anchorY: 78,
   align: "left",
   active: true,
  },
  {
   id: "license",
   label: "No License &\nNo Registration",
   x: 67,
   y: 58,
   labelX: 79,
   labelY: 61,
   anchorX: 71,
   anchorY: 59,
   align: "left",
   active: true,
  },
  {
   id: "guard",
   label: "Protective\nGuard",
   x: 65,
   y: 43,
   labelX: 80,
   labelY: 39,
   anchorX: 69,
   anchorY: 43,
   align: "left",
   active: true,
  },
  {
   id: "comfort",
   label: "Comfort\nSpacious",
   x: 56,
   y: 39,
   labelX: 72,
   labelY: 16,
   anchorX: 60,
   anchorY: 34,
   align: "left",
   active: true,
  },
 ],
};

const labelAlignClass = {
 left: "text-left",
 right: "text-right -translate-x-full",
 center: "text-center -translate-x-1/2",
};

function getFallbackArrowEnd(feature) {
 const gap = 1.6;
 const labelX = Number(feature.labelX ?? 50);
 const labelY = Number(feature.labelY ?? 50);
 if (feature.align === "left") return { x: Math.max(0, labelX - gap), y: labelY };
 if (feature.align === "right") return { x: Math.min(100, labelX + gap), y: labelY };
 return { x: labelX, y: labelY };
}

function getArrowEnd(feature) {
 const fallback = getFallbackArrowEnd(feature);
 const x = Number.isFinite(Number(feature.arrowX)) ? Number(feature.arrowX) : fallback.x;
 const y = Number.isFinite(Number(feature.arrowY)) ? Number(feature.arrowY) : fallback.y;
 return `${x},${y}`;
}

function clamp(value) {
 const number = Number(value);
 if (!Number.isFinite(number)) return 50;
 return Math.min(100, Math.max(0, Number(number.toFixed(2))));
}

function normalizeFeature(feature, index) {
 return {
  id: feature.id || `feature-${Date.now()}-${index}`,
  label: feature.label || `Feature ${index + 1}`,
  x: clamp(feature.x),
  y: clamp(feature.y),
  labelX: clamp(feature.labelX),
  labelY: clamp(feature.labelY),
  anchorX: clamp(feature.anchorX ?? feature.x),
  anchorY: clamp(feature.anchorY ?? feature.y),
  arrowX: clamp(feature.arrowX ?? getFallbackArrowEnd(feature).x),
  arrowY: clamp(feature.arrowY ?? getFallbackArrowEnd(feature).y),
  align: ["left", "right", "center"].includes(feature.align)
   ? feature.align
   : "left",
  active: feature.active !== false,
 };
}

export default function ScootyFeatureEditor({ config, saveConfig }) {
 const [draft, setDraft] = useState(defaultConfig);
 const [selectedId, setSelectedId] = useState(defaultConfig.features[0].id);
 const [dragTarget, setDragTarget] = useState(null);
 const [saving, setSaving] = useState(false);
 const [savedAt, setSavedAt] = useState("");
 const features = useMemo(() => draft.features || [], [draft.features]);
 const selectedFeature =
  features.find((feature) => feature.id === selectedId) || features[0];

 useEffect(() => {
  const nextConfig = config || defaultConfig;
  const nextFeatures = (
   Array.isArray(nextConfig.features) ? nextConfig.features : defaultConfig.features
  ).map(normalizeFeature);
  setDraft({
   image: nextConfig.image || defaultConfig.image,
   active: nextConfig.active !== false,
   features: nextFeatures,
  });
  setSelectedId(nextFeatures[0]?.id || "");
 }, [config]);

 const activeFeatures = useMemo(
  () => features.filter((feature) => feature.active !== false),
  [features],
 );

 function updateDraft(updates) {
  setSavedAt("");
  setDraft((current) => ({ ...current, ...updates }));
 }

 function updateFeature(featureId, updates) {
  setSavedAt("");
  setDraft((current) => ({
   ...current,
   features: current.features.map((feature) =>
    feature.id === featureId ? { ...feature, ...updates } : feature,
   ),
  }));
 }

 function addFeature() {
  const feature = normalizeFeature(
   {
    id: `feature-${Date.now()}`,
    label: "New Feature",
    x: 50,
    y: 50,
    labelX: 62,
    labelY: 50,
    anchorX: 55,
    anchorY: 50,
    arrowX: 60,
    arrowY: 50,
    align: "left",
    active: true,
   },
   features.length,
  );
  setDraft((current) => ({
   ...current,
   features: [...current.features, feature],
  }));
  setSelectedId(feature.id);
 }

 function removeFeature(featureId) {
  const nextFeatures = features.filter((feature) => feature.id !== featureId);
  setDraft((current) => ({ ...current, features: nextFeatures }));
  setSelectedId(nextFeatures[0]?.id || "");
 }

 function updatePosition(event, target) {
  const rect = event.currentTarget.getBoundingClientRect();
  const x = clamp(((event.clientX - rect.left) / rect.width) * 100);
  const y = clamp(((event.clientY - rect.top) / rect.height) * 100);
  const updates =
   target === "label"
    ? { labelX: x, labelY: y }
    : target === "arrow"
      ? { arrowX: x, arrowY: y }
      : target === "anchor"
        ? { anchorX: x, anchorY: y }
        : { x, y };
  updateFeature(selectedId, updates);
 }

 function handlePointerMove(event) {
  if (!dragTarget || !selectedId) return;
  updatePosition(event, dragTarget);
 }

 async function save() {
  setSaving(true);
  const payload = {
   ...draft,
   features: features.map((feature, index) => normalizeFeature(feature, index)),
  };
  const didSave = await saveConfig(payload);
  if (didSave) {
   setDraft(payload);
   setSavedAt(
    new Date().toLocaleTimeString("en-IN", {
     hour: "2-digit",
     minute: "2-digit",
    }),
   );
  }
  setSaving(false);
 }

 return (
  <section className="page-stack">
   <div className="page-toolbar">
    <div>
     <p>Homepage Content</p>
     <h1>Scooty Features</h1>
     <small>
      Point, label aur arrow ko drag karke set karein. Save ke baad same layout
      website par dikhega.
     </small>
    </div>
    <div className="scooty-save-actions">
     {savedAt ? <span>Saved at {savedAt}</span> : null}
     <button type="button" onClick={save} disabled={saving}>
      <FiSave /> {saving ? "Saving..." : "Save Layout"}
     </button>
    </div>
   </div>

   <div className="scooty-editor-layout">
    <article className="admin-card scooty-editor-stage-card">
     <div className="scooty-editor-stage-head">
      <label>
       Image URL
       <input
        value={draft.image}
        onChange={(event) => updateDraft({ image: event.target.value })}
       />
      </label>
      <label className="toggle-row">
       <input
        type="checkbox"
        checked={draft.active}
        onChange={(event) => updateDraft({ active: event.target.checked })}
       />
       Show section on website
      </label>
      <button
       className="scooty-inline-save"
       type="button"
       onClick={save}
       disabled={saving}
      >
       <FiSave /> {saving ? "Saving..." : "Save Layout"}
      </button>
      {savedAt ? (
       <span className="scooty-inline-saved">Saved at {savedAt}</span>
      ) : null}
     </div>

     <div
      className="scooty-editor-stage"
      onPointerMove={handlePointerMove}
      onPointerUp={() => setDragTarget(null)}
      onPointerLeave={() => setDragTarget(null)}
     >
      <img src={draft.image} alt="Scooty feature editor" draggable="false" />
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
       <defs>
        <marker
         id="admin-scooty-arrow-head"
         markerHeight="12"
         markerWidth="12"
         orient="auto"
         refX="10"
         refY="5"
         viewBox="0 0 10 10"
        >
         <path d="M1,1 L10,5 L1,9" fill="none" stroke="#020617" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" />
        </marker>
       </defs>
       {activeFeatures.map((feature) => (
        <polyline
         key={feature.id}
         points={`${feature.x},${feature.y} ${feature.anchorX},${feature.anchorY} ${getArrowEnd(feature)}`}
         fill="none"
         markerEnd="url(#admin-scooty-arrow-head)"
         stroke="#020617"
         strokeWidth={selectedId === feature.id ? "3.5" : "3.1"}
         strokeLinecap="round"
         strokeLinejoin="round"
         vectorEffect="non-scaling-stroke"
        />
       ))}
      </svg>
      {activeFeatures.map((feature) => (
       <button
        key={`${feature.id}-point`}
        type="button"
        className={
         selectedId === feature.id
          ? "feature-hotspot selected"
          : "feature-hotspot"
        }
        style={{ left: `${feature.x}%`, top: `${feature.y}%` }}
        onClick={() => setSelectedId(feature.id)}
        onPointerDown={(event) => {
         event.preventDefault();
         setSelectedId(feature.id);
         setDragTarget("point");
        }}
       >
        +
       </button>
      ))}
      {activeFeatures.map((feature) => (
       <button
        key={`${feature.id}-arrow`}
        type="button"
        className={
         selectedId === feature.id
          ? "feature-arrow-end selected"
          : "feature-arrow-end"
        }
        style={{
         left: `${feature.arrowX ?? getFallbackArrowEnd(feature).x}%`,
         top: `${feature.arrowY ?? getFallbackArrowEnd(feature).y}%`,
        }}
        onClick={() => setSelectedId(feature.id)}
        onPointerDown={(event) => {
         event.preventDefault();
         setSelectedId(feature.id);
         setDragTarget("arrow");
        }}
        title="Drag arrow end"
       />
      ))}
      {activeFeatures.map((feature) => (
       <button
        key={`${feature.id}-anchor`}
        type="button"
        className={
         selectedId === feature.id
          ? "feature-anchor selected"
          : "feature-anchor"
        }
        style={{ left: `${feature.anchorX}%`, top: `${feature.anchorY}%` }}
        onClick={() => setSelectedId(feature.id)}
        onPointerDown={(event) => {
         event.preventDefault();
         setSelectedId(feature.id);
         setDragTarget("anchor");
        }}
        title="Drag arrow start"
       />
      ))}
      {activeFeatures.map((feature) => (
       <button
        key={`${feature.id}-label`}
        type="button"
        className={`feature-label ${selectedId === feature.id ? "selected" : ""} ${labelAlignClass[feature.align]}`}
        style={{ left: `${feature.labelX}%`, top: `${feature.labelY}%` }}
        onClick={() => setSelectedId(feature.id)}
        onPointerDown={(event) => {
         event.preventDefault();
         setSelectedId(feature.id);
         setDragTarget("label");
        }}
       >
        {feature.label}
       </button>
      ))}
     </div>
    </article>

    <aside className="admin-card scooty-editor-panel">
     <div className="scooty-editor-panel-head">
      <strong>Feature points</strong>
      <button type="button" onClick={addFeature}>
       <FiPlus /> Add
      </button>
     </div>
     <div className="feature-list">
      {features.map((feature) => (
       <button
        key={feature.id}
        type="button"
        className={selectedId === feature.id ? "active" : ""}
        onClick={() => setSelectedId(feature.id)}
       >
        <span>{feature.label.replace(/\n/g, " ")}</span>
        <small>
         {Math.round(feature.x)}, {Math.round(feature.y)}
        </small>
       </button>
      ))}
     </div>

     {selectedFeature ? (
      <div className="feature-form">
       <label>
        Label
        <textarea
         rows="2"
         value={selectedFeature.label}
         onChange={(event) =>
          updateFeature(selectedFeature.id, { label: event.target.value })
         }
        />
       </label>
       <label>
        Text align
        <select
         value={selectedFeature.align}
         onChange={(event) =>
          updateFeature(selectedFeature.id, { align: event.target.value })
         }
        >
         <option value="left">Left</option>
         <option value="right">Right</option>
         <option value="center">Center</option>
        </select>
       </label>
       <label className="toggle-row">
        <input
         type="checkbox"
         checked={selectedFeature.active !== false}
         onChange={(event) =>
          updateFeature(selectedFeature.id, { active: event.target.checked })
         }
        />
        Active
       </label>
       <div className="coordinate-grid">
        {["x", "y", "anchorX", "anchorY", "arrowX", "arrowY", "labelX", "labelY"].map((key) => (
         <label key={key}>
          {key}
          <input
           type="number"
           min="0"
           max="100"
           step="0.1"
           value={selectedFeature[key]}
           onChange={(event) =>
            updateFeature(selectedFeature.id, {
             [key]: clamp(event.target.value),
            })
           }
          />
         </label>
        ))}
       </div>
       <button
        className="danger"
        type="button"
        onClick={() => removeFeature(selectedFeature.id)}
       >
        <FiTrash2 /> Delete selected
       </button>
      </div>
     ) : null}
    </aside>
   </div>
  </section>
 );
}











